import { useNavigate } from 'react-router'
import { Bot } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAgents, type AgentSummary } from '../api/agents'
import { Badge } from '../components/ui/Badge'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { formatPhone } from '../lib/utils'

export function AgentsPage() {
  const { t } = useTranslation('agents')
  const navigate = useNavigate()
  const { data, isLoading, isError, refetch } = useAgents()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-text-primary md:text-2xl">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-secondary">{t('list.subtitle')}</p>
        </div>
      </div>

      {isLoading && <SpinLoader label={t('list.loading')} />}

      {isError && <ErrorCard message={t('list.loadFailed')} onRetry={() => refetch()} />}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <Bot className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">{t('list.empty')}</p>
        </div>
      )}

      {data && data.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-surface-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-3">{t('list.table.name')}</th>
                  <th className="px-4 py-3">{t('list.table.purpose')}</th>
                  <th className="px-4 py-3">{t('list.table.phoneNumbers')}</th>
                  <th className="px-4 py-3">{t('list.table.language')}</th>
                  <th className="px-4 py-3">{t('list.table.publishedVersion')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((agent) => (
                  <AgentRow
                    key={agent.agent_id}
                    agent={agent}
                    onClick={() => navigate(`/app/agents/${encodeURIComponent(agent.agent_id)}`)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {data.map((agent) => (
              <AgentCard
                key={agent.agent_id}
                agent={agent}
                onClick={() => navigate(`/app/agents/${encodeURIComponent(agent.agent_id)}`)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Kind/purpose descriptor shown as a Badge. Prefers `purpose`, falls back to
 *  `kind` (inbound/outbound/both), and renders nothing when neither exists. */
function AgentBadge({ agent }: { agent: AgentSummary }) {
  const label = agent.purpose || agent.kind
  if (!label) return <span className="text-text-muted">—</span>
  const tone = agent.kind === 'inbound' ? 'info' : agent.kind === 'outbound' ? 'flamingo' : 'neutral'
  return <Badge tone={tone}>{label}</Badge>
}

function AgentRow({ agent, onClick }: { agent: AgentSummary; onClick: () => void }) {
  const { t } = useTranslation('agents')
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-border/50 transition-colors hover:bg-surface-elevated/50"
    >
      <td className="px-4 py-3 font-medium text-text-primary">
        <span className="inline-flex items-center gap-2">
          <Bot className="h-4 w-4 text-text-muted" />
          {agent.agent_name || <span className="font-mono text-text-secondary">{agent.agent_id}</span>}
        </span>
      </td>
      <td className="px-4 py-3">
        <AgentBadge agent={agent} />
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {agent.phone_numbers?.length ? (
          <span className="flex flex-col whitespace-nowrap">
            {agent.phone_numbers.map((n) => (
              <span key={n}>{formatPhone(n)}</span>
            ))}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {agent.language || <span className="text-text-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {agent.published_version != null ? (
          t('list.versionTag', { version: agent.published_version })
        ) : (
          <span className="text-text-muted">—</span>
        )}
      </td>
    </tr>
  )
}

function AgentCard({ agent, onClick }: { agent: AgentSummary; onClick: () => void }) {
  const { t } = useTranslation('agents')
  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-lg border border-border bg-surface-card p-4 transition-colors hover:bg-surface-elevated/50"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-medium text-text-primary">
          <Bot className="h-4 w-4 text-text-muted" />
          {agent.agent_name || <span className="font-mono text-sm text-text-secondary">{agent.agent_id}</span>}
        </span>
        <AgentBadge agent={agent} />
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-text-muted">
        {!!agent.phone_numbers?.length && (
          <span>{agent.phone_numbers.map(formatPhone).join(' · ')}</span>
        )}
        {agent.language && <span>{agent.language}</span>}
        {agent.published_version != null && (
          <span>{t('list.versionTag', { version: agent.published_version })}</span>
        )}
      </div>
    </div>
  )
}
